import { createTabBar } from '../components/tab-bar.js';
import { getRouter } from '../router.js';
import type { Schedule, StopId, Trip } from '../bus/types.js';
import {
  nextTrip,
  upcomingTrips,
  tripsForStop,
  minutesUntilTrip,
  formatCountdown,
  timeToMinutes,
  nowInMinutes,
} from '../bus/schedule.js';

let timerIntervalId: number | undefined;

function createEl<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (cls) el.className = cls;
  return el;
}

function renderTripPill(trip: Trip, stop: StopId, nextTripId: string | null): HTMLElement {
  const pill = createEl('div', 'bus-trip-pill');
  pill.textContent = trip[stop] ?? '';
  const tripTime = timeToMinutes(trip[stop]);
  const now = nowInMinutes();
  if (trip.id === nextTripId) {
    pill.classList.add('bus-trip-pill--next');
  } else if (tripTime !== null && tripTime < now) {
    pill.classList.add('bus-trip-pill--past');
  }
  return pill;
}

function renderTimerCard(trip: Trip, stop: StopId): HTMLElement {
  const card = createEl('div', 'bus-timer-card');

  const timeRow = createEl('div', 'bus-timer-card__row');

  const timeEl = createEl('span', 'bus-timer-card__time');
  timeEl.textContent = trip[stop] ?? '';
  timeRow.appendChild(timeEl);

  const badge = createEl('span', 'bus-timer-card__badge');
  badge.textContent = formatCountdown(minutesUntilTrip(trip, stop) ?? 0);
  timeRow.appendChild(badge);

  card.appendChild(timeRow);

  const label = createEl('div', 'bus-timer-card__label');
  label.textContent = `Отправление в ${trip[stop]}`;
  card.appendChild(label);

  const progress = createEl('div', 'bus-timer-card__progress');
  const progressFill = createEl('div', 'bus-timer-card__progress-fill');
  const minutes = minutesUntilTrip(trip, stop) ?? 1;
  const interval = 20 * 60;
  const elapsed = Math.max(0, interval - minutes * 60);
  progressFill.style.width = `${Math.min(100, (elapsed / interval) * 100)}%`;
  progress.appendChild(progressFill);
  card.appendChild(progress);

  return card;
}

function renderNoMoreTrips(): HTMLElement {
  const el = createEl('div', 'bus-no-more');
  el.textContent = 'Сегодня автобусов больше нет...';
  return el;
}

function renderEmptyState(): HTMLElement {
  const el = createEl('div', 'bus-empty');
  el.textContent = 'Нет рейсов для этой остановки';
  return el;
}

function renderTripGrid(trips: Trip[], stop: StopId, currentTrip: Trip | null): HTMLElement {
  const grid = createEl('div', 'bus-trip-grid');
  const nextId = currentTrip?.id ?? null;
  for (const trip of trips) {
    grid.appendChild(renderTripPill(trip, stop, nextId));
  }
  return grid;
}

export async function busPage(): Promise<HTMLElement> {
  const router = getRouter();

  const page = createEl('div', 'page page--bus');
  page.setAttribute('data-title', 'Автобус');

  // Header
  const header = createEl('header', 'page-header');
  header.innerHTML = `
  <svg class="page-header__logo" width="32" height="28" viewBox="0 0 32 28" fill="none" aria-hidden="true">
    <polyline points="2,14 16,2 30,14" stroke="#4a7c2f" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <rect x="6" y="14" width="20" height="12" rx="1" stroke="#4a7c2f" stroke-width="1.5" fill="none"/>
    <rect x="13" y="19" width="6" height="7" rx="1" stroke="#4a7c2f" stroke-width="1.5" fill="none"/>
  </svg>
  <div class="page-header__text">
    <h1 class="page-header__title">СНТ "ЭЛЕКТРОН"</h1>
    <span class="page-header__subtitle">Расписание автобуса</span>
  </div>`;
  page.appendChild(header);

  // Tabs
  const featureListings = import.meta.env.VITE_FEATURE_LISTINGS === 'true';
  const tabs = createTabBar([
    { id: 'news', label: 'Новости', href: '/' },
    { id: 'contacts', label: 'Контакты', href: '/contacts' },
    { id: 'board', label: 'Объявления', href: '/board', hidden: !featureListings },
    { id: 'bus', label: 'Автобус', href: '/bus' },
  ], 'bus', (href) => router.navigate(href));
  page.appendChild(tabs);

  // Content area
  const content = createEl('div', 'bus-content');
  page.appendChild(content);

  // Load schedule
  let schedule: Schedule;
  try {
    const resp = await fetch('/bus/schedule.json');
    if (!resp.ok) throw new Error('Failed to load schedule');
    schedule = await resp.json() as Schedule;
  } catch {
    const err = createEl('div', 'empty-state');
    err.innerHTML = '<p>Ошибка загрузки расписания</p>';
    content.appendChild(err);
    return page;
  }

  // State
  let currentStop: StopId = (localStorage.getItem('bus-stop') as StopId) || 'sady_lesnoe';
  let showAll = false;

  function saveStop() {
    localStorage.setItem('bus-stop', currentStop);
  }

  // Stop switcher
  const switcher = createEl('div', 'bus-stop-switcher');
  for (const stop of schedule.stops) {
    const btn = createEl('button', 'bus-stop-btn');
    if (stop.id === currentStop) btn.classList.add('bus-stop-btn--active');
    btn.textContent = stop.name;
    btn.addEventListener('click', () => {
      currentStop = stop.id;
      showAll = false;
      saveStop();
      rerenderBusContent();
    });
    switcher.appendChild(btn);
  }
  content.appendChild(switcher);

  // Dynamic section
  const busMain = createEl('div', 'bus-main');
  content.appendChild(busMain);

  // Show all button
  const showAllBtn = createEl('button', 'bus-show-all-btn');
  content.appendChild(showAllBtn);

  // Grid container
  const gridContainer = createEl('div', 'bus-grid-container');
  content.appendChild(gridContainer);

  function updateTimerDisplay(): void {
    const trip = nextTrip(schedule, currentStop);

    // Update badge & progress in timer card
    const timerCard = busMain.querySelector('.bus-timer-card');
    if (timerCard && trip) {
      const badge = timerCard.querySelector('.bus-timer-card__badge');
      if (badge) badge.textContent = formatCountdown(minutesUntilTrip(trip, currentStop) ?? 0);

      const fill = timerCard.querySelector<HTMLElement>('.bus-timer-card__progress-fill');
      if (fill) {
        const minutes = minutesUntilTrip(trip, currentStop) ?? 1;
        const interval = 20 * 60;
        const elapsed = Math.max(0, interval - minutes * 60);
        fill.style.width = `${Math.min(100, (elapsed / interval) * 100)}%`;
      }
    }

    // Re-render grid to update past/next pills
    renderGrid();
  }

  function renderGrid(): void {
    const trip = nextTrip(schedule, currentStop);
    const trips = showAll
      ? tripsForStop(schedule, currentStop)
      : upcomingTrips(schedule, currentStop, 3);
    const newGrid = renderTripGrid(trips, currentStop, trip);
    gridContainer.innerHTML = '';
    gridContainer.appendChild(newGrid);
  }

  function rerenderBusContent(): void {
    // Clear interval
    if (timerIntervalId !== undefined) {
      clearInterval(timerIntervalId);
      timerIntervalId = undefined;
    }

    // Update stop switcher active state
    switcher.querySelectorAll('.bus-stop-btn').forEach((btn, i) => {
      const stop = schedule.stops[i];
      if (stop) {
        btn.classList.toggle('bus-stop-btn--active', stop.id === currentStop);
      }
    });

    // Render main area
    busMain.innerHTML = '';
    const trip = nextTrip(schedule, currentStop);
    const allTrips = schedule.trips.filter(t => t[currentStop] !== null);

    if (trip) {
      busMain.appendChild(renderTimerCard(trip, currentStop));
    } else if (allTrips.length > 0) {
      busMain.appendChild(renderNoMoreTrips());
    } else {
      busMain.appendChild(renderEmptyState());
    }

    // Show all btn
    showAllBtn.textContent = showAll ? 'Скрыть расписание ↑' : 'Показать всё расписание ↓';

    // Grid
    renderGrid();

    // Start timer
    timerIntervalId = window.setInterval(updateTimerDisplay, 1000);
  }

  showAllBtn.addEventListener('click', () => {
    showAll = !showAll;
    showAllBtn.textContent = showAll ? 'Скрыть расписание ↑' : 'Показать всё расписание ↓';
    renderGrid();
  });

  rerenderBusContent();

  // Cleanup timer when page is replaced
  const observer = new MutationObserver(() => {
    if (!document.contains(page)) {
      if (timerIntervalId !== undefined) {
        clearInterval(timerIntervalId);
        timerIntervalId = undefined;
      }
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return page;
}
